// Scenes made from a description: the layers a theme names (a sky, a moon,
// mountains, a city, water, rain...) drawn with the same pixel-art pieces as
// Meadow and Summit, so a scene an agent describes looks like it belongs.
// Everything here is drawing from data. Nothing a theme says can run.

import type { SceneLayer, SceneSpec } from "../../shared/types";
import {
  BAYER,
  css,
  ditherGlow,
  ditherSky,
  disc,
  drawStars,
  fillRidge,
  fractal,
  grain,
  hex,
  layer,
  makeStars,
  pine,
  pixelCloud,
  pixelMoon,
  ridge,
  rng,
  roundTree,
  type RGB,
  type Scene,
  type Star,
} from "./scenes";

// ---------------------------------------------------------------------------
// colour

const HEX = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i;

/** A colour a theme gave, as #rrggbb, or the fallback when it is not one. */
export function color(v: unknown, fallback: string): string {
  if (typeof v !== "string" || !HEX.test(v.trim())) return fallback;
  const s = v.trim().toLowerCase();
  return s.length === 4 ? `#${s[1]}${s[1]}${s[2]}${s[2]}${s[3]}${s[3]}` : s;
}

const toHex = (c: RGB) => `#${c.map((x) => Math.max(0, Math.min(255, Math.round(x))).toString(16).padStart(2, "0")).join("")}`;

export function mix(a: string, b: string, t: number): string {
  const A = hex(a);
  const B = hex(b);
  return toHex([A[0] + (B[0] - A[0]) * t, A[1] + (B[1] - A[1]) * t, A[2] + (B[2] - A[2]) * t]);
}

export const lighten = (c: string, t: number) => mix(c, "#ffffff", t);
export const darken = (c: string, t: number) => mix(c, "#000000", t);

const num = (v: unknown, lo: number, hi: number, fallback: number) => (typeof v === "number" && Number.isFinite(v) ? Math.max(lo, Math.min(hi, v)) : fallback);

function lum(c: string): number {
  const [r, g, b] = hex(c).map((x) => {
    const s = x / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

// ---------------------------------------------------------------------------
// a spec with every gap filled, every number in range and every colour real

interface Layer extends Required<Pick<SceneLayer, "kind" | "color" | "at" | "height" | "rough" | "x">> {
  light: string | null;
  snow: string | null;
  windows: string | null;
  trees: { kind: "pine" | "round" | "palm" | "cherry"; color: string; light: string; density: number } | null;
}

export interface Plan {
  style: "pixel" | "abstract";
  seed: number;
  sky: string[];
  horizon: number;
  dark: boolean;
  body: { kind: "moon" | "sun" | "planet"; x: number; y: number; size: number; color: string; glow: string | null; ring: string | null } | null;
  stars: { density: number; color: string } | null;
  aurora: { colors: string[]; strength: number } | null;
  clouds: { color: string; light: string; shade: string; count: number; at: number; speed: number } | null;
  layers: Layer[];
  water: { color: string; light: string; at: number } | null;
  grid: { color: string; at: number } | null;
  particles: { kind: NonNullable<SceneSpec["particles"]>["kind"]; color: string; density: number } | null;
  flowers: string[];
  fog: { color: string; at: number; amount: number } | null;
  blobs: { x: number; y: number; r: number; color: string }[];
  grain: number;
  vignette: number;
}

const KINDS = new Set(["mountains", "peak", "hills", "dunes", "city", "village", "lighthouse", "forest", "plain", "cliffs"]);
const PARTICLES = new Set(["fireflies", "snow", "rain", "petals", "embers", "leaves", "dust", "bubbles"]);

export function plan(spec: SceneSpec | null | undefined, opts: { dark?: boolean; seed?: number } = {}): Plan {
  const s = spec ?? {};
  const dark = opts.dark ?? true;
  const fallbackSky = dark ? ["#0a1028", "#16214c", "#2a3a74", "#3c5290"] : ["#5ea8ec", "#8cc6f4", "#bfe0fb", "#e6f4fd"];
  const sky = (Array.isArray(s.sky) ? s.sky : []).slice(0, 8).map((c, i) => color(c, fallbackSky[Math.min(i, fallbackSky.length - 1)]));
  const skyStops = sky.length >= 2 ? sky : sky.length === 1 ? [darken(sky[0], 0.35), sky[0]] : fallbackSky;
  const horizon = num(s.horizon, 0.35, 0.92, 0.66);
  const lowSky = skyStops[skyStops.length - 1];

  let body: Plan["body"] = null;
  const bk = s.body?.kind ?? (s.body ? "moon" : dark ? "moon" : "sun");
  if (bk !== "none" && (s.body || s.style !== "abstract")) {
    const kind = bk === "sun" || bk === "planet" ? bk : "moon";
    const base = kind === "sun" ? "#ffe2a0" : kind === "planet" ? "#7a6ad8" : "#ece6cf";
    const c = color(s.body?.color, base);
    body = {
      kind,
      x: num(s.body?.x, 0.04, 0.96, 0.78),
      y: num(s.body?.y, 0.04, 0.95, kind === "sun" ? 0.26 : 0.17),
      size: num(s.body?.size, 0.02, 0.35, kind === "planet" ? 0.16 : 0.055),
      color: c,
      glow: s.body?.glow === undefined ? lighten(mix(c, lowSky, 0.4), 0.1) : s.body.glow ? color(s.body.glow, c) : null,
      ring: s.body?.ring ? color(s.body.ring, lighten(c, 0.3)) : null,
    };
  }

  const stars = s.stars ? { density: num(s.stars.density, 0, 1, 0.5), color: color(s.stars.color, dark ? "#eef2ff" : "#ffffff") } : dark && s.stars === undefined ? { density: 0.45, color: "#eef2ff" } : null;
  const aurora = s.aurora && Array.isArray(s.aurora.colors) && s.aurora.colors.length ? { colors: s.aurora.colors.slice(0, 4).map((c) => color(c, "#72f0c6")), strength: num(s.aurora.strength, 0.1, 1, 0.6) } : null;
  const clouds = s.clouds
    ? {
        color: color(s.clouds.color, mix(lowSky, "#ffffff", 0.2)),
        light: color(s.clouds.light, lighten(color(s.clouds.color, lowSky), 0.2)),
        shade: color(s.clouds.shade, darken(color(s.clouds.color, lowSky), 0.15)),
        count: Math.round(num(s.clouds.count, 0, 12, 5)),
        at: num(s.clouds.at, 0.05, 0.8, 0.28),
        speed: num(s.clouds.speed, 0, 3, 1),
      }
    : null;

  const given = (Array.isArray(s.layers) ? s.layers : []).filter((l) => l && KINDS.has(l.kind)).slice(0, 7);
  const ground = darken(lowSky, dark ? 0.55 : 0.35);
  const layers: Layer[] = (
    given.length
      ? given
      : s.style === "abstract"
        ? []
        : ([
            { kind: "mountains", at: horizon - 0.02, height: 0.15, color: mix(lowSky, ground, 0.45) },
            { kind: "hills", at: horizon + 0.09, height: 0.07, color: mix(lowSky, ground, 0.72) },
            { kind: "plain", at: Math.min(0.95, horizon + 0.2), height: 0.03, color: ground },
          ] as SceneLayer[])
  )
    .map((l, i) => {
      const c = color(l.color, mix(lowSky, ground, 0.4 + i * 0.2));
      return {
        kind: l.kind,
        color: c,
        light: l.light === undefined ? lighten(c, dark ? 0.1 : 0.12) : l.light ? color(l.light, lighten(c, 0.1)) : null,
        at: num(l.at, 0.2, 0.98, horizon + i * 0.08),
        height: num(l.height, 0, 0.5, l.kind === "mountains" ? 0.16 : l.kind === "city" ? 0.18 : 0.06),
        rough: num(l.rough, 0, 1, l.kind === "mountains" ? 0.6 : 0.3),
        x: num(l.x, 0, 1, l.kind === "lighthouse" ? 0.1 : 0.5),
        snow: l.snow ? color(l.snow, "#f2f4ff") : null,
        windows: l.kind === "city" || l.kind === "village" || l.kind === "lighthouse" ? color(l.windows, l.kind === "lighthouse" ? "#ffe6a0" : "#ffc06a") : null,
        trees: l.trees
          ? {
              kind: (["pine", "round", "palm", "cherry"] as const).find((k) => k === l.trees!.kind) ?? (l.kind === "forest" ? "pine" : "round"),
              color: color(l.trees.color, darken(c, 0.25)),
              light: color(l.trees.light, lighten(color(l.trees.color, darken(c, 0.25)), 0.15)),
              density: num(l.trees.density, 0, 1, l.kind === "forest" ? 0.85 : 0.4),
            }
          : l.kind === "forest"
            ? { kind: "pine" as const, color: darken(c, 0.3), light: lighten(darken(c, 0.3), 0.12), density: 0.85 }
            : null,
      };
    })
    .sort((a, b) => a.at - b.at);

  const water = s.water ? { color: color(s.water.color, mix(lowSky, "#0a1430", 0.5)), light: color(s.water.light, lighten(color(s.water.color, lowSky), 0.35)), at: num(s.water.at, 0.3, 0.95, horizon) } : null;
  const grid = s.grid ? { color: color(s.grid.color, "#ff5ccd"), at: num(s.grid.at, 0.3, 0.95, horizon) } : null;
  const particles = s.particles && PARTICLES.has(s.particles.kind) ? { kind: s.particles.kind, color: color(s.particles.color, defaultParticle(s.particles.kind)), density: num(s.particles.density, 0, 1, 0.5) } : null;
  const flowers = (Array.isArray(s.flowers) ? s.flowers : []).slice(0, 6).map((c) => color(c, "#ffffff"));
  const fog = s.fog ? { color: color(s.fog.color, lowSky), at: num(s.fog.at, 0.2, 0.98, horizon), amount: num(s.fog.amount, 0, 1, 0.5) } : null;
  const blobs = (Array.isArray(s.blobs) ? s.blobs : []).slice(0, 6).map((b) => ({ x: num(b?.x, -0.2, 1.2, 0.5), y: num(b?.y, -0.2, 1.2, 0.3), r: num(b?.r, 0.05, 1.2, 0.5), color: color(b?.color, skyStops[1] ?? skyStops[0]) }));
  return {
    style: s.style === "abstract" ? "abstract" : "pixel",
    seed: Math.round(num(s.seed, 0, 1e9, opts.seed ?? 7)),
    sky: skyStops,
    horizon,
    dark,
    body,
    stars,
    aurora,
    clouds,
    layers,
    water,
    grid,
    particles,
    flowers,
    fog,
    blobs,
    grain: num(s.grain, 0, 1, s.style === "abstract" ? 0.35 : 0),
    vignette: num(s.vignette, 0, 1, s.style === "abstract" ? 0.4 : 0),
  };
}

function defaultParticle(kind: string): string {
  return { fireflies: "#e6ffaa", snow: "#ffffff", rain: "#a8c4ff", petals: "#ffb7d0", embers: "#ffb060", leaves: "#e8964a", dust: "#fff2d0", bubbles: "#bfe9ff" }[kind] ?? "#ffffff";
}

// ---------------------------------------------------------------------------
// pixel landscapes

interface Built {
  sky: HTMLCanvasElement;
  back: HTMLCanvasElement;
  front: HTMLCanvasElement;
  stars: Star[];
  clouds: { x: number; y: number; speed: number; art: HTMLCanvasElement }[];
  lights: { x: number; y: number; p: number; c: string }[];
  /** a lighthouse's lamp, whose beam turns in the frame loop; front when it stands before the water */
  beams: { x: number; y: number; c: string; reach: number; p: number; front: boolean; night: boolean }[];
  sparkles: { x: number; y: number; p: number; len: number }[];
  motes: { x: number; y: number; p: number; s: number; z: number }[];
  waterTop: number;
  gridTop: number;
  sunX: number;
  sunR: number;
  /** the color of the light's road across the water */
  road: string;
}

function heightsFor(l: Layer, w: number, h: number, seed: number): number[] {
  const base = h * l.at;
  const amp = h * l.height;
  switch (l.kind) {
    case "mountains": {
      // peaks sharpened from fractal noise, so ranges read as peaks and valleys, not a plateau
      const n = fractal(seed, 5);
      const f = 0.0055 + l.rough * 0.011;
      return Array.from({ length: w }, (_, x) => Math.round(base - amp * Math.pow(Math.max(0, n(x * f) * 1.25 - 0.18), 1.45) * 1.15));
    }
    case "cliffs": {
      const raw = ridge(w, base, amp, 0.006 + l.rough * 0.008, seed, 3);
      const step = Math.max(2, Math.round(h * 0.018));
      return raw.map((y) => Math.round(y / step) * step);
    }
    case "dunes": {
      const r = rng(seed);
      const f1 = 0.012 + r() * 0.01;
      const f2 = f1 * (1.9 + r() * 0.6);
      const p1 = r() * 10;
      const p2 = r() * 10;
      return Array.from({ length: w }, (_, x) => Math.round(base - amp * (0.55 + 0.3 * Math.sin(x * f1 + p1) + 0.15 * Math.sin(x * f2 + p2))));
    }
    case "peak": {
      // one mountain on its own, like Fuji: steep near the summit, long slopes, a little noise
      const cx = w * l.x;
      const spread = w * (0.22 + (1 - l.rough) * 0.18);
      const wobble = ridge(w, 0, h * 0.012 * (0.5 + l.rough), 0.05, seed, 2);
      return Array.from({ length: w }, (_, x) => {
        const d = Math.abs(x - cx) / spread;
        const shape = d < 1 ? Math.pow(1 - d, 1.6) : 0;
        return Math.round(base - amp * shape + wobble[x] * (d < 1 ? 1 : 0.3));
      });
    }
    case "plain":
      return ridge(w, base, Math.max(1, amp * 0.6), 0.02, seed, 2);
    case "forest":
      return ridge(w, base, amp * 0.6, 0.01 + l.rough * 0.01, seed, 3);
    case "hills":
    case "city":
    default:
      return ridge(w, base, amp, 0.007 + l.rough * 0.01, seed, 3);
  }
}

/**
 * A village on gentle ground: cabins with pitched roofs, lit windows, snow on
 * the roofs if it has snow, and smoke from the chimneys. rough 0 is one cabin,
 * standing at x; toward 1, a row of houses.
 */
function drawVillage(ctx: CanvasRenderingContext2D, l: Layer, w: number, h: number, r: () => number, lights: Built["lights"]): number[] {
  const ground = ridge(w, Math.round(h * l.at), Math.max(1, h * 0.012), 0.012, Math.floor(r() * 1e6), 2);
  fillRidge(ctx, ground, h, l.color, l.light ?? undefined);
  const k = h / 205;
  const at: number[] = [];
  if (l.rough < 0.15) at.push(Math.round(w * l.x));
  else {
    const gap = Math.max(10, Math.round((70 - l.rough * 52) * k));
    for (let x = Math.round(r() * gap); x < w; x += gap + Math.floor(r() * gap * 0.7)) at.push(x);
  }
  const big = (l.rough < 0.15 ? 2.1 : 1) * (0.7 + l.height * 3);
  const top = ground.slice();
  for (const cx of at) {
    const bw = Math.max(6, Math.round((9 + r() * 6) * k * big));
    const bh = Math.max(4, Math.round((5 + r() * 3) * k * big));
    const x0 = cx - Math.floor(bw / 2);
    let base = 0;
    for (let x = x0; x < x0 + bw; x++) base = Math.max(base, ground[Math.max(0, Math.min(w - 1, x))]);
    const wall = base - bh;
    const roofH = Math.max(3, Math.ceil(bw * 0.42));
    // warm light from the windows, on the ground in front
    if (l.windows) {
      const glow = mix(l.windows, l.color, 0.55);
      for (let gy = 0; gy < Math.max(2, Math.round(3 * k * big)); gy++)
        for (let gx = -bw; gx <= bw; gx++) {
          const d = Math.abs(gx) / bw + gy / Math.max(2, 3 * k * big);
          if (d < 1 && BAYER[gy & 3][(cx + gx) & 3] > d * 1.15) {
            ctx.fillStyle = glow;
            ctx.fillRect(cx + gx, base + 1 + gy, 1, 1);
          }
        }
    }
    // walls, of logs
    ctx.fillStyle = l.color;
    ctx.fillRect(x0, wall, bw, base - wall + 1);
    ctx.fillStyle = darken(l.color, 0.22);
    for (let y = wall + 2; y < base; y += 2) ctx.fillRect(x0, y, bw, 1);
    // a door
    if (bw >= 10) {
      const dw = Math.max(2, Math.round(bw * 0.18));
      ctx.fillStyle = darken(l.color, 0.35);
      ctx.fillRect(x0 + bw - dw - Math.max(1, Math.round(bw * 0.14)), base - Math.max(3, Math.round(bh * 0.6)), dw, Math.max(3, Math.round(bh * 0.6)) + 1);
    }
    // the roof, wider than the walls, lit from the left
    for (let i = 0; i <= roofH; i++) {
      const half = Math.round(((bw / 2 + 1) * i) / roofH);
      const y = wall - roofH + i;
      ctx.fillStyle = darken(l.color, 0.12);
      ctx.fillRect(cx - half, y, half * 2 + 1, 1);
      if (l.light) {
        ctx.fillStyle = l.light;
        ctx.fillRect(cx - half, y, 1, 1);
      }
      if (l.snow && i <= Math.max(1, Math.round(roofH * 0.45))) {
        ctx.fillStyle = l.snow;
        ctx.fillRect(cx - half, y, half * 2 + 1, 1);
      }
      for (let x = cx - half; x <= cx + half; x++) if (x >= 0 && x < w) top[x] = Math.min(top[x], y);
    }
    // a chimney, and its smoke
    const chx = cx + Math.round(bw * 0.22);
    const chTop = wall - Math.round(roofH * 0.55) - 2;
    ctx.fillStyle = darken(l.color, 0.2);
    ctx.fillRect(chx, chTop, Math.max(1, Math.round(k * 1.5)), Math.round(roofH * 0.55) + 2);
    const smoke = mix(l.light ?? lighten(l.color, 0.3), "#ffffff", 0.35);
    for (let i = 0; i < 4; i++) {
      ctx.globalAlpha = 0.5 - i * 0.1;
      ctx.fillStyle = smoke;
      ctx.fillRect(chx + Math.round(i * 1.4 + r()), chTop - 2 - i * 2, 1 + (i > 1 ? 1 : 0), 1);
    }
    ctx.globalAlpha = 1;
    // windows, warm, and one that flickers like a fire inside
    const win = Math.max(1, Math.round(1.6 * k * big));
    const n = bw > 14 * k ? 2 : 1;
    // the door takes the right, so windows sit to its left
    const span = bw >= 10 ? Math.round(bw * 0.7) : bw;
    for (let i = 0; i < n; i++) {
      const wx = x0 + Math.round(((i + 1) * span) / (n + 1)) - Math.floor(win / 2);
      const wy = wall + Math.max(1, Math.round(bh * 0.3));
      ctx.fillStyle = l.windows!;
      ctx.fillRect(wx, wy, win, win);
      ctx.fillStyle = lighten(l.windows!, 0.3);
      ctx.fillRect(wx, wy, 1, 1);
      if (i === 0) lights.push({ x: wx, y: wy, p: r() * 6, c: lighten(l.windows!, 0.2) });
    }
  }
  return top;
}

/**
 * A headland with a lighthouse on it at x: a tower in white and red bands, a
 * gallery, the lamp and its cap. The lamp's beam turns in the frame loop.
 * height is the headland's; the tower stands a little taller than it.
 */
function drawLighthouse(ctx: CanvasRenderingContext2D, l: Layer, w: number, h: number, r: () => number, beams: Built["beams"], front: boolean): number[] {
  const k = h / 205;
  const cx = Math.round(w * l.x);
  const base = Math.round(h * l.at);
  const amp = Math.max(4, h * l.height);
  // a rounded top around the tower, falling away as rock, then a sea cliff: past it, the water shows
  const spread = w * (0.15 + l.rough * 0.1);
  const noise = ridge(w, 0, Math.max(1, h * 0.012 * (0.5 + l.rough)), 0.05, Math.floor(r() * 1e6), 2);
  const ground = Array.from({ length: w }, (_, x) => {
    const d = Math.abs(x - cx) / spread;
    if (d >= 1.08) return h + 2;
    if (d >= 1) return Math.round(base + (h - base) * ((d - 1) / 0.08) ** 0.7);
    const shape = d < 0.25 ? 1 : Math.pow(1 - (d - 0.25) / 0.75, 0.5);
    return Math.round(base - amp * shape + noise[x]);
  });
  fillRidge(ctx, ground, h, l.color, l.light ?? undefined);
  const top = ground.slice();
  // at night the tower takes the dark of the scene, by day it stands out white
  const night = lum(l.color) < 0.06;
  const white = mix("#eee8dc", l.color, night ? 0.32 : 0.06);
  const red = mix("#c4473a", l.color, night ? 0.32 : 0.06);
  const th = Math.max(10, Math.round((22 + l.height * 40) * k));
  const bw = Math.max(3, Math.round(5.2 * k));
  const tw = Math.max(3, Math.round(3.6 * k));
  const foot = ground[Math.max(0, Math.min(w - 1, cx))] + 1;
  for (let y = 0; y < th; y++) {
    const ww = Math.round(bw + (tw - bw) * (y / th));
    const x0 = cx - Math.floor(ww / 2);
    const fill = Math.floor((y / th) * 6) % 2 === 1 ? red : white;
    ctx.fillStyle = fill;
    ctx.fillRect(x0, foot - y, ww, 1);
    // the side away from the light
    ctx.fillStyle = darken(fill, 0.2);
    ctx.fillRect(x0 + ww - 1, foot - y, 1, 1);
    for (let x = x0; x < x0 + ww; x++) if (x >= 0 && x < w) top[x] = Math.min(top[x], foot - y);
  }
  // the gallery, the lamp and its cap
  const iron = darken(l.color, 0.35);
  const gy = foot - th;
  ctx.fillStyle = iron;
  ctx.fillRect(cx - Math.floor(tw / 2) - 1, gy, tw + 2, 1);
  const lampH = Math.max(2, Math.round(2.6 * k));
  ctx.fillStyle = l.windows!;
  ctx.fillRect(cx - Math.floor(tw / 2), gy - lampH, tw, lampH);
  ctx.fillStyle = iron;
  for (let i = 0; i * 2 < tw; i++) ctx.fillRect(cx - Math.floor(tw / 2) + i, gy - lampH - 1 - i, tw - i * 2, 1);
  for (let x = cx - tw; x <= cx + tw; x++) if (x >= 0 && x < w) top[x] = Math.min(top[x], gy - lampH - 2);
  beams.push({ x: cx, y: gy - Math.ceil(lampH / 2), c: l.windows!, reach: Math.round(w * 0.5), p: r() * 6, front, night });
  return top;
}

/** The beam: it sweeps across as the lamp turns, shortest when it points at us or away, and the lamp flares as it passes us. */
function drawBeam(ctx: CanvasRenderingContext2D, bm: Built["beams"][number], t: number, w: number) {
  const rgb = hex(bm.c);
  // by day the lamp is only a lamp
  if (!bm.night) {
    ctx.fillStyle = css(rgb, 0.9);
    ctx.fillRect(bm.x, bm.y, 1, 1);
    return;
  }
  const a = t * 0.85 + bm.p;
  const across = Math.cos(a);
  const toward = Math.sin(a) > 0;
  const len = Math.abs(across) * bm.reach;
  const dir = across < 0 ? -1 : 1;
  const strength = toward ? 0.55 : 0.26;
  for (let i = 2; i < len; i++) {
    const x = Math.round(bm.x + dir * i);
    if (x < 0 || x >= w) break;
    const spread = Math.floor(i * 0.06);
    const fade = strength * (1 - i / bm.reach) * (0.4 + 0.6 * Math.abs(across));
    if (fade < 0.02) break;
    ctx.fillStyle = css(rgb, fade);
    ctx.fillRect(x, bm.y - spread, 1, spread * 2 + 1);
  }
  const flare = toward ? Math.max(0, 1 - Math.abs(across) * 3) : 0;
  if (flare > 0.05) {
    ctx.fillStyle = css(rgb, 0.3 * flare);
    ctx.fillRect(bm.x - 3, bm.y - 3, 7, 7);
  }
  ctx.fillStyle = css(rgb, 0.6 + 0.4 * flare);
  ctx.fillRect(bm.x - 1, bm.y - 1, 3, 3);
}

function drawLayer(ctx: CanvasRenderingContext2D, l: Layer, w: number, h: number, seed: number, lights: Built["lights"], beams: Built["beams"], front: boolean): number[] {
  const r = rng(seed * 3 + 1);
  if (l.kind === "city") {
    // a skyline: blocks of every height, roofs caught by light, windows
    const base = Math.round(h * l.at);
    const top = Array.from({ length: w }, () => base);
    let x = -Math.floor(r() * 6);
    while (x < w) {
      const bw = 4 + Math.floor(r() * 9);
      const tall = r() < 0.18;
      const bh = Math.round(h * l.height * (tall ? 0.75 + r() * 0.35 : 0.25 + r() * 0.5));
      const y = base - bh;
      ctx.fillStyle = l.color;
      ctx.fillRect(x, y, bw, h - y);
      if (l.light) {
        ctx.fillStyle = l.light;
        ctx.fillRect(x, y, bw, 1);
      }
      if (tall && bw > 5) {
        ctx.fillStyle = l.color;
        ctx.fillRect(x + Math.floor(bw / 2), y - 3, 1, 3);
        lights.push({ x: x + Math.floor(bw / 2), y: y - 4, p: r() * 6, c: "#ff5a5a" });
      }
      for (let wy = y + 2; wy < base - 1; wy += 2)
        for (let wx = x + 1; wx < x + bw - 1; wx += 2) {
          const lit = r();
          if (lit < 0.32) {
            ctx.fillStyle = lit < 0.04 ? lighten(l.windows!, 0.35) : lit < 0.2 ? l.windows! : mix(l.windows!, l.color, 0.45);
            ctx.fillRect(wx, wy, 1, 1);
            if (lit < 0.012) lights.push({ x: wx, y: wy, p: r() * 6, c: l.windows! });
          }
        }
      for (let i = x; i < x + bw && i < w; i++) if (i >= 0) top[i] = Math.min(top[i], y);
      x += bw + (r() < 0.3 ? 1 : 0);
    }
    ctx.fillStyle = l.color;
    ctx.fillRect(0, base, w, h - base);
    return top;
  }
  if (l.kind === "village") return drawVillage(ctx, l, w, h, r, lights);
  if (l.kind === "lighthouse") return drawLighthouse(ctx, l, w, h, r, beams, front);
  const heights = heightsFor(l, w, h, seed);
  fillRidge(ctx, heights, h, l.color, l.light ?? undefined);
  if ((l.kind === "mountains" || l.kind === "peak") && l.snow) {
    const peak = Math.min(...heights);
    const ragged = fractal(seed + 9, 3);
    const shade = mix(l.snow, l.color, 0.4);
    const taper = fractal(seed + 21, 2);
    heights.forEach((y, x) => {
      // the snowline wanders, and dips into gullies
      const line = peak + h * l.height * (0.26 + ragged(x * 0.08) * 0.22);
      if (y >= line) return;
      // light from the left: a face whose ground rises to the right catches it
      const lit = (heights[Math.max(0, x - 3)] ?? y) > (heights[Math.min(w - 1, x + 3)] ?? y);
      // a cap follows its ridge down, deepest under the summits, so it never ends in a level slab
      const depth = Math.round((line - y) * (0.5 + taper(x * 0.11) * 0.35) + 1);
      ctx.fillStyle = lit ? l.snow! : shade;
      ctx.fillRect(x, y, 1, Math.max(1, depth));
      // streaks of snow below the line, the way gullies hold it
      if (r() < 0.12) ctx.fillRect(x, y + depth, 1, 1 + Math.floor(r() * 3));
    });
  }
  texture(ctx, heights, l, w, h, r);
  if (l.kind === "dunes" && l.light) {
    // the lee side of each crest sits in shadow
    const shadow = darken(l.color, 0.18);
    heights.forEach((y, x) => {
      const next = heights[x + 1] ?? y;
      if (next > y) {
        ctx.fillStyle = shadow;
        ctx.fillRect(x, y + 1, 1, Math.min(6, Math.round(h * 0.02)));
      }
    });
  }
  if (l.trees && l.trees.density > 0) {
    const t = l.trees;
    const gap = Math.max(2, Math.round(14 - t.density * 12));
    for (let x = 2; x < w; x += gap + Math.floor(r() * gap)) {
      if (r() > 0.35 + t.density * 0.65) continue;
      const base = heights[Math.min(w - 1, x)] + 1;
      const scale = (h / 205) * (0.7 + r() * 0.6);
      if (t.kind === "pine") pine(ctx, x, base, Math.round((6 + r() * 10) * scale * (l.kind === "forest" ? 1.2 : 1)), t.color, t.light);
      else if (t.kind === "palm") palm(ctx, x, base, Math.round((10 + r() * 6) * scale), t.color);
      else if (t.kind === "cherry") roundTree(ctx, x, base, Math.max(2, Math.round((2 + r() * 2.5) * scale)), t.color, t.light, darken(l.color, 0.4));
      else roundTree(ctx, x, base, Math.max(2, Math.round((2 + r() * 2.5) * scale)), t.color, t.light, darken(t.color, 0.35));
    }
  }
  return heights;
}

/**
 * What makes a filled shape read as ground: it darkens toward its foot,
 * dithered, and carries small marks. Grass on hills and plains, rock on
 * mountains and cliffs, ripples on dunes.
 */
function texture(ctx: CanvasRenderingContext2D, heights: number[], l: Layer, w: number, h: number, r: () => number) {
  if (l.kind === "city") return;
  const deep = darken(l.color, 0.24);
  const depth = Math.max(6, h * 0.22);
  for (let x = 0; x < w; x++) {
    const top = heights[x];
    for (let y = top + 2; y < Math.min(h, top + depth * 1.6); y++) {
      const t = (y - top) / depth;
      if (t * 0.75 > BAYER[y & 3][x & 3] + 0.15) {
        ctx.fillStyle = deep;
        ctx.fillRect(x, y, 1, 1);
      }
    }
  }
  const mark = l.light ?? lighten(l.color, 0.1);
  const dark = darken(l.color, 0.14);
  for (let x = 0; x < w; x++) {
    const top = heights[x];
    if (l.kind === "hills" || l.kind === "plain" || l.kind === "forest") {
      // tufts along the edge
      if (r() < 0.55) {
        ctx.fillStyle = mark;
        ctx.fillRect(x, top - (r() < 0.3 ? 2 : 1), 1, r() < 0.5 ? 2 : 1);
      }
      for (let y = top + 2; y < h; y += 2 + Math.floor(r() * 5)) {
        if (r() < 0.2) {
          ctx.fillStyle = r() < 0.5 ? mix(l.color, mark, 0.5) : dark;
          ctx.fillRect(x, y, 1, 1 + Math.floor(r() * 2));
        }
      }
    } else if (l.kind === "mountains" || l.kind === "cliffs" || l.kind === "peak") {
      for (let y = top + 3; y < h; y += 3 + Math.floor(r() * 6)) {
        if (r() < 0.12) {
          ctx.fillStyle = dark;
          ctx.fillRect(x, y, 1, 1 + Math.floor(r() * 3));
        }
      }
    } else if (l.kind === "dunes") {
      for (let y = top + 3; y < h; y += 4 + Math.floor(r() * 5)) {
        if (r() < 0.16) {
          ctx.fillStyle = mix(l.color, mark, 0.6);
          ctx.fillRect(x, y, 2 + Math.floor(r() * 3), 1);
        }
      }
    }
  }
}

function palm(ctx: CanvasRenderingContext2D, x: number, base: number, h: number, c: string) {
  const top = base - h;
  ctx.fillStyle = c;
  for (let y = top; y < base; y++) ctx.fillRect(x + Math.round(((y - top) / h) * 2), y, 1, 1);
  for (const [dx, dy] of [
    [-5, 2], [-4, 1], [-3, 0], [-2, -1], [-1, -1], [0, -1], [1, -1], [2, -1], [3, 0], [4, 1], [5, 2], [-6, 3], [6, 3],
  ])
    ctx.fillRect(x + dx, top + dy, 1, 1);
}

function moonTones(c: string): string[] {
  return [darken(c, 0.32), darken(c, 0.18), c, lighten(c, 0.12), lighten(c, 0.24)];
}

function pixelBuild(p: Plan, w: number, h: number): Built {
  const r = rng(p.seed);
  const horizon = Math.round(h * p.horizon);
  const waterTop = p.water ? Math.round(h * p.water.at) : h;
  const [sky, sctx] = layer(w, h);
  ditherSky(sctx, w, 0, Math.min(horizon, waterTop), p.sky);
  sctx.fillStyle = p.sky[p.sky.length - 1];
  sctx.fillRect(0, Math.min(horizon, waterTop), w, h);
  let sunX = Math.round(w * 0.5);
  let sunR = 0;
  if (p.body) {
    const b = p.body;
    const bx = Math.round(w * b.x);
    const by = Math.round(h * b.y);
    const br = Math.max(4, Math.round(h * b.size));
    sunX = bx;
    sunR = br;
    if (b.glow) ditherGlow(sctx, w, h, bx, by, br * (b.kind === "sun" ? 3.4 : 4.2), hex(b.glow), b.kind === "sun" ? 1.25 : 1.05);
    if (b.kind === "moon") {
      const art = pixelMoon(br, moonTones(b.color));
      sctx.drawImage(art, bx - br - 1, by - br - 1);
    } else if (b.kind === "sun") {
      disc(sctx, bx, by, br, b.color);
      disc(sctx, bx, by - 1, Math.max(1, br - 2), lighten(b.color, 0.3));
      // a low sun is sliced by thin bands of haze
      if (by + br > Math.min(horizon, waterTop) - h * 0.06) {
        sctx.fillStyle = mix(b.color, p.sky[p.sky.length - 1], 0.55);
        for (let i = 0; i < 4; i++) sctx.fillRect(bx - br - 2, by + Math.round(br * (0.2 + i * 0.2)), br * 2 + 4, 1 + (i > 1 ? 1 : 0));
      }
    } else {
      // a planet: shaded from the top left, with a ring if it has one
      const dark = darken(b.color, 0.55);
      for (let y = -br; y <= br; y++)
        for (let x = -br; x <= br; x++) {
          const d = (x * x + y * y) / (br * br);
          if (d > 1) continue;
          const light = 1 - (x + y + br * 0.8) / (br * 2.6);
          ctx2(sctx, bx + x, by + y, light + (BAYER[(y + 64) & 3][(x + 64) & 3] - 0.5) * 0.18 > 0.45 ? b.color : light > 0.25 ? mix(b.color, dark, 0.5) : dark);
        }
      if (b.ring) {
        sctx.fillStyle = b.ring;
        for (let a = 0; a < Math.PI * 2; a += 0.01) {
          const rx = Math.cos(a) * br * 1.8;
          const ry = Math.sin(a) * br * 0.42;
          if (ry < 0 && rx * rx + (ry / 0.42) ** 2 < br * br) continue; // behind the planet
          sctx.fillRect(Math.round(bx + rx), Math.round(by + ry), 1, 1);
        }
      }
    }
  }

  const stars = p.stars ? makeStars(w, h, Math.round((p.stars.density * w * h) / 170), Math.min(horizon, waterTop) / h * 0.92, p.seed + 5) : [];

  const clouds = p.clouds
    ? Array.from({ length: p.clouds.count }, (_, i) => {
        const size = (3 + r() * 4) * (h / 205);
        const [cv, cctx] = layer(Math.ceil(size * 9), Math.ceil(size * 3));
        pixelCloud(cctx, cv.width / 2, cv.height * 0.6, size, p.clouds!.color, p.clouds!.light, p.clouds!.shade, rng(p.seed + 40 + i));
        return { x: r() * w, y: h * Math.max(0.03, p.clouds!.at + (r() - 0.5) * 0.24), speed: (0.25 + r() * 0.5) * p.clouds!.speed, art: cv };
      })
    : [];

  // the land: everything behind the water on one sheet, everything in front on another
  const [back, bctx] = layer(w, h);
  const [front, fctx] = layer(w, h);
  const lights: Built["lights"] = [];
  const beams: Built["beams"] = [];
  const behind = p.layers.filter((l) => !p.water || l.at <= p.water.at + 0.005);
  const before = p.layers.filter((l) => p.water && l.at > p.water.at + 0.005);
  let nearest: number[] | null = null;
  behind.forEach((l, i) => {
    nearest = drawLayer(bctx, l, w, h, p.seed + 11 + i * 7, lights, beams, false);
  });
  if (p.water) {
    const w2 = p.water;
    ditherSky(bctx, w, waterTop, h, [mix(w2.color, p.sky[p.sky.length - 1], 0.35), w2.color, darken(w2.color, 0.25), darken(w2.color, 0.45)]);
    // what stands on the shore, mirrored in the water: every other row, a little adrift, fading down
    const reach = Math.min(h - waterTop, Math.round(h * 0.22));
    const [copy] = layer(w, h);
    copy.getContext("2d")!.drawImage(back, 0, 0);
    for (let y = 0; y < reach; y += 2) {
      const src = waterTop - 1 - y;
      if (src < 0) break;
      bctx.globalAlpha = 0.34 * (1 - y / reach);
      bctx.drawImage(copy, 0, src, w, 1, Math.round(Math.sin(y * 0.7) * 1.4), waterTop + y, w, 1);
    }
    bctx.globalAlpha = 1;
  }
  if (p.fog) {
    // a band of mist that thins upward, dithered so it reads as pixels
    const f = p.fog;
    const y1 = Math.round(h * f.at);
    const y0 = Math.max(0, Math.round(y1 - h * 0.18));
    const fc = hex(f.color);
    const img = bctx.getImageData(0, y0, w, Math.max(1, y1 - y0 + Math.round(h * 0.04)));
    for (let y = 0; y < img.height; y++)
      for (let x = 0; x < w; x++) {
        const t = Math.min(1, (y / Math.max(1, y1 - y0)) ** 1.6) * f.amount * 0.75;
        if (t <= BAYER[(y0 + y) & 3][x & 3] * 0.9) continue;
        const o = (y * w + x) * 4;
        if (img.data[o + 3] === 0) continue;
        img.data[o] += (fc[0] - img.data[o]) * 0.55;
        img.data[o + 1] += (fc[1] - img.data[o + 1]) * 0.55;
        img.data[o + 2] += (fc[2] - img.data[o + 2]) * 0.55;
      }
    bctx.putImageData(img, 0, y0);
  }
  before.forEach((l, i) => {
    nearest = drawLayer(fctx, l, w, h, p.seed + 61 + i * 7, lights, beams, true);
  });
  // flowers on whatever ground is nearest
  if (p.flowers.length && nearest) {
    const ground = nearest as number[];
    const ctx = before.length ? fctx : bctx;
    for (let i = 0; i < w * 0.8; i++) {
      const x = Math.floor(r() * w);
      const depth = r() ** 0.6;
      const y = Math.round(ground[x] + 3 + depth * (h - ground[x] - 4));
      if (y >= h) continue;
      ctx.fillStyle = p.flowers[Math.floor(r() * p.flowers.length)];
      ctx.fillRect(x, y, 1, 1);
      if (depth > 0.75 && r() < 0.45) {
        ctx.fillRect(x - 1, y, 1, 1);
        ctx.fillRect(x + 1, y, 1, 1);
        ctx.fillRect(x, y - 1, 1, 1);
      }
    }
  }
  const sparkles = p.water ? Array.from({ length: Math.round(w * 0.35) }, () => ({ x: r(), y: r() ** 1.4, p: r() * 6, len: 1 + Math.floor(r() * 4) })) : [];
  const count = p.particles ? Math.round(((w * h) / 900) * (0.2 + p.particles.density * 1.6)) : 0;
  const motes = Array.from({ length: count }, () => ({ x: r() * w, y: r() * h, p: r() * 10, s: 0.4 + r() * 0.9, z: r() }));
  // with no sun or moon, a lighthouse's lamp lays the road across the water
  const lamp = !p.body ? beams[0] : undefined;
  const road = p.body?.glow ?? (lamp && p.water ? mix(lamp.c, p.water.light, 0.35) : (p.water?.light ?? "#ffffff"));
  return { sky, back, front, stars, clouds, lights, beams, sparkles, motes, waterTop, gridTop: p.grid ? Math.round(h * p.grid.at) : h, sunX: lamp ? lamp.x : sunX, sunR: lamp ? 3 : sunR, road };
}

function ctx2(ctx: CanvasRenderingContext2D, x: number, y: number, c: string) {
  ctx.fillStyle = c;
  ctx.fillRect(x, y, 1, 1);
}

function pixelAurora(ctx: CanvasRenderingContext2D, w: number, h: number, t: number, a: NonNullable<Plan["aurora"]>, horizon: number) {
  ctx.globalCompositeOperation = "lighter";
  a.colors.forEach((c, bi) => {
    const rgb = hex(c);
    const yBase = h * (0.18 + bi * 0.06);
    for (let x = 0; x < w; x++) {
      const u = x / w;
      const yc = yBase + Math.sin(u * Math.PI * (1.4 + bi * 0.6) + t * 0.12 * (bi % 2 ? -1 : 1)) * h * 0.07 + Math.sin(u * 9 + t * 0.2) * h * 0.015;
      const hh = h * (0.16 - bi * 0.025) * (0.7 + 0.3 * Math.sin(u * 7 + t * 0.3 + bi));
      const k = a.strength * (0.55 + 0.45 * Math.sin(u * 13 + t * 0.5 + bi * 2));
      for (let y = Math.max(0, Math.round(yc - hh)); y < Math.min(horizon, Math.round(yc + hh * 0.2)); y++) {
        const f = y < yc ? (y - (yc - hh)) / hh : 1 - (y - yc) / (hh * 0.2);
        const alpha = Math.round(f * k * 0.5 * 4 + BAYER[y & 3][x & 3] - 0.5) / 4;
        if (alpha <= 0) continue;
        ctx.fillStyle = css(rgb, Math.min(0.55, alpha * 0.5));
        ctx.fillRect(x, y, 1, 1);
      }
    }
  });
  ctx.globalCompositeOperation = "source-over";
}

function pixelGrid(ctx: CanvasRenderingContext2D, w: number, h: number, t: number, top: number, c: string) {
  const vx = w / 2;
  ctx.fillStyle = c;
  for (let i = -16; i <= 16; i++) {
    const x0 = vx + i * 3;
    const x1 = vx + i * w * 0.09;
    for (let y = top; y < h; y++) {
      const k = (y - top) / (h - top);
      ctx.fillRect(Math.round(x0 + (x1 - x0) * k), y, 1, 1);
    }
  }
  const speed = (t * 0.35) % 1;
  for (let i = 0; i < 12; i++) {
    const k = (i + speed) / 12;
    const y = Math.round(top + (h - top) * k * k);
    ctx.globalAlpha = Math.min(1, k * 2.4);
    ctx.fillRect(0, y, w, 1);
  }
  ctx.globalAlpha = 1;
}

function pixelParticles(ctx: CanvasRenderingContext2D, w: number, h: number, t: number, b: Built, p: NonNullable<Plan["particles"]>) {
  const rgb = hex(p.color);
  for (const m of b.motes) {
    switch (p.kind) {
      case "fireflies": {
        const blink = Math.sin(t * 1.7 * m.s + m.p);
        if (blink < 0.25) break;
        const x = Math.round(m.x + Math.sin(t * 0.35 * m.s + m.p) * 9);
        const y = Math.round(h * 0.62 + m.z * h * 0.34 + Math.cos(t * 0.5 * m.s + m.p * 2) * 4);
        ctx.fillStyle = css(rgb, blink > 0.75 ? 0.95 : 0.65);
        ctx.fillRect(x, y, 1, 1);
        if (blink > 0.88) {
          ctx.fillStyle = css(rgb, 0.22);
          ctx.fillRect(x - 1, y, 3, 1);
          ctx.fillRect(x, y - 1, 1, 3);
        }
        break;
      }
      case "snow": {
        const y = Math.round((m.y + t * (5 + m.s * 9)) % (h + 4)) - 2;
        const x = Math.round((m.x + Math.sin(t * 0.6 + m.p) * 4 + t * 2) % w);
        ctx.fillStyle = css(rgb, 0.55 + m.z * 0.45);
        ctx.fillRect(x, y, 1, 1);
        if (m.z > 0.82) ctx.fillRect(x + 1, y, 1, 1);
        break;
      }
      case "rain": {
        const y = Math.round((m.y + t * (70 + m.s * 50)) % (h + 8)) - 4;
        const x = Math.round((m.x - t * 14 + y * 0.18) % w + w) % w;
        ctx.fillStyle = css(rgb, 0.25 + m.z * 0.35);
        ctx.fillRect(x, y, 1, 2 + Math.round(m.z * 2));
        break;
      }
      case "petals":
      case "leaves": {
        const y = Math.round((m.y + t * (6 + m.s * 7)) % (h + 6)) - 3;
        const x = Math.round(((m.x + t * (8 + m.s * 6) + Math.sin(t * 1.1 + m.p) * 6) % (w + 6) + w + 6) % (w + 6)) - 3;
        const flip = Math.sin(t * 3 * m.s + m.p) > 0;
        ctx.fillStyle = css(rgb, 0.85);
        ctx.fillRect(x, y, flip ? 2 : 1, 1);
        if (p.kind === "leaves") {
          ctx.fillStyle = css(rgb, 0.5);
          ctx.fillRect(x, y + 1, 1, 1);
        }
        break;
      }
      case "embers": {
        const y = Math.round(h - ((m.y + t * (8 + m.s * 12)) % (h * 0.9)));
        const x = Math.round(m.x + Math.sin(t * 0.8 + m.p) * 5);
        const fade = Math.min(1, (y / h) * 1.6);
        ctx.fillStyle = css(rgb, (0.4 + 0.5 * Math.sin(t * 4 + m.p * 3) ** 2) * fade);
        ctx.fillRect(x, y, 1, 1);
        break;
      }
      case "bubbles": {
        const y = Math.round(h - ((m.y + t * (5 + m.s * 6)) % h));
        const x = Math.round(m.x + Math.sin(t * 1.2 + m.p) * 3);
        ctx.fillStyle = css(rgb, 0.5);
        ctx.fillRect(x, y - 1, 1, 1);
        ctx.fillRect(x - 1, y, 1, 1);
        ctx.fillRect(x + 1, y, 1, 1);
        ctx.fillRect(x, y + 1, 1, 1);
        break;
      }
      case "dust": {
        const x = Math.round((m.x + t * (1 + m.s)) % w);
        const y = Math.round(m.y + Math.sin(t * 0.4 + m.p) * 6);
        ctx.fillStyle = css(rgb, 0.18 + 0.3 * Math.sin(t * 0.9 + m.p) ** 2);
        ctx.fillRect(x, y, 1, 1);
        break;
      }
    }
  }
}

function pixelScene(p: Plan): Scene {
  const moving = !!(p.stars || p.clouds || p.aurora || p.water || p.grid || p.particles || p.layers.some((l) => l.kind === "lighthouse"));
  return {
    pixel: true,
    fps: moving ? (p.particles?.kind === "rain" ? 14 : 10) : 0,
    build: (w, h) => pixelBuild(p, w, h) as unknown as Record<string, unknown>,
    frame({ ctx, w, h, t }, state) {
      const b = state as unknown as Built;
      ctx.drawImage(b.sky, 0, 0);
      if (p.stars) drawStars(ctx, b.stars, t, hex(p.stars.color));
      if (p.aurora) pixelAurora(ctx, w, h, t, p.aurora, Math.round(h * p.horizon));
      for (const c of b.clouds) {
        const x = ((c.x + t * c.speed * 2) % (w + c.art.width)) - c.art.width;
        ctx.drawImage(c.art, Math.round(x), Math.round(c.y));
      }
      ctx.drawImage(b.back, 0, 0);
      if (p.water) {
        // the sun's or moon's road across the water, and small waves
        const sea = h - b.waterTop;
        const road = b.road;
        for (const sp of b.sparkles) {
          const y = b.waterTop + 1 + Math.floor(sp.y * Math.max(1, sea - 2));
          const spread = Math.max(6, b.sunR) * (0.6 + sp.y * 2.2);
          const x = Math.round(b.sunX + (sp.x - 0.5) * 2 * spread + Math.sin(t * 1.3 + sp.p) * 2);
          if (Math.sin(t * 2 + sp.p * 3) < -0.2) continue;
          ctx.fillStyle = sp.y < 0.4 ? lighten(road, 0.25) : road;
          ctx.fillRect(x, y, sp.len + (sp.y > 0.6 ? 2 : 0), 1);
        }
        ctx.fillStyle = css(hex(p.water.light), 0.35);
        for (let i = 0; i < 24; i++) {
          const y = b.waterTop + 3 + ((i * 37) % Math.max(4, sea - 4));
          const x = Math.round(((i * 97 + t * (4 + (i % 3))) % (w + 20)) - 10);
          ctx.fillRect(x, y, 3 + (i % 4), 1);
        }
      }
      for (const bm of b.beams) if (!bm.front) drawBeam(ctx, bm, t, w);
      if (p.grid) pixelGrid(ctx, w, h, t, b.gridTop, p.grid.color);
      ctx.drawImage(b.front, 0, 0);
      for (const bm of b.beams) if (bm.front) drawBeam(ctx, bm, t, w);
      for (const l of b.lights) {
        if (Math.sin(t * 1.4 + l.p) < 0.1) continue;
        ctx.fillStyle = l.c;
        ctx.fillRect(l.x, l.y, 1, 1);
      }
      if (p.particles) pixelParticles(ctx, w, h, t, b, p.particles);
    },
  };
}

// ---------------------------------------------------------------------------
// abstract: gradients, pools of light, grain

function abstractScene(p: Plan): Scene {
  return {
    pixel: false,
    fps: p.stars || p.particles || p.aurora || p.grid ? 24 : p.blobs.length ? 12 : 0,
    build(w, h) {
      const r = rng(p.seed);
      const [neb, nctx] = layer(Math.max(1, Math.round(w / 3)), Math.max(1, Math.round(h / 3)));
      for (const b of p.blobs) {
        const g = nctx.createRadialGradient(neb.width * b.x, neb.height * b.y, 0, neb.width * b.x, neb.height * b.y, Math.max(neb.width, neb.height) * b.r);
        g.addColorStop(0, css(hex(b.color), 0.75));
        g.addColorStop(1, css(hex(b.color), 0));
        nctx.fillStyle = g;
        nctx.fillRect(0, 0, neb.width, neb.height);
      }
      const stars = p.stars ? Array.from({ length: Math.round(((w * h) / 3200) * p.stars.density * 2) }, () => ({ x: r() * w, y: r() * r() * h * 0.9, b: 0.25 + r() * 0.75, s: 0.5 + r() * 2, p: r() * 6, size: r() < 0.08 ? 1.6 : 1 })) : [];
      const count = p.particles ? Math.round(((w * h) / 26000) * (0.4 + p.particles.density * 2)) + 12 : 0;
      const motes = Array.from({ length: count }, () => ({ x: r() * w, y: r() * h, s: 6 + r() * 18, p: r() * 10, size: 0.6 + r() * 1.8, a: 0.25 + r() * 0.6 }));
      return { neb, stars, motes, grain: p.grain ? grain(160, Math.round(6 + p.grain * 22)) : null };
    },
    frame({ ctx, w, h, t }, state) {
      const st = state as { neb: HTMLCanvasElement; stars: { x: number; y: number; b: number; s: number; p: number; size: number }[]; motes: { x: number; y: number; s: number; p: number; size: number; a: number }[]; grain: HTMLCanvasElement | null };
      const sky = ctx.createLinearGradient(0, 0, 0, h);
      p.sky.forEach((c, i) => sky.addColorStop(i / Math.max(1, p.sky.length - 1), c));
      ctx.fillStyle = sky;
      ctx.fillRect(0, 0, w, h);
      if (p.blobs.length) {
        ctx.globalAlpha = 0.85 + 0.15 * Math.sin(t * 0.2);
        ctx.drawImage(st.neb, 0, 0, w, h);
        ctx.globalAlpha = 1;
      }
      if (p.stars) {
        const sc = hex(p.stars.color);
        for (const sd of st.stars) {
          ctx.fillStyle = css(sc, sd.b * (0.6 + 0.4 * Math.sin(t * sd.s + sd.p)));
          ctx.fillRect(sd.x, sd.y, sd.size, sd.size);
        }
      }
      if (p.aurora) smoothAurora(ctx, w, h, t, p.aurora);
      if (p.body) smoothBody(ctx, w, h, p.body);
      if (p.grid) smoothGrid(ctx, w, h, t, h * p.grid.at, p.grid.color);
      if (p.particles) {
        const pc = hex(p.particles.color);
        ctx.globalCompositeOperation = "lighter";
        for (const m of st.motes) {
          const rising = p.particles.kind === "embers" || p.particles.kind === "bubbles" || p.particles.kind === "fireflies" || p.particles.kind === "dust";
          const y = rising ? ((((m.y - t * m.s) % (h + 40)) + h + 40) % (h + 40)) - 20 : (((m.y + t * m.s * 2) % (h + 40)) + h + 40) % (h + 40) - 20;
          const x = m.x + Math.sin(t * 0.4 + m.p) * 14;
          const flicker = 0.6 + 0.4 * Math.sin(t * 3 + m.p * 7);
          ctx.fillStyle = css(pc, m.a * flicker);
          ctx.beginPath();
          ctx.arc(x, y, m.size, 0, Math.PI * 2);
          ctx.fill();
        }
        ctx.globalCompositeOperation = "source-over";
      }
      if (st.grain) {
        const pat = ctx.createPattern(st.grain, "repeat");
        if (pat) {
          ctx.fillStyle = pat;
          ctx.fillRect(0, 0, w, h);
        }
      }
      if (p.vignette) {
        const v = ctx.createRadialGradient(w / 2, h / 2, Math.min(w, h) * 0.35, w / 2, h / 2, Math.max(w, h) * 0.8);
        v.addColorStop(0, "rgba(0,0,0,0)");
        v.addColorStop(1, `rgba(0,0,0,${(p.dark ? 0.6 : 0.18) * p.vignette})`);
        ctx.fillStyle = v;
        ctx.fillRect(0, 0, w, h);
      }
    },
  };
}

function smoothAurora(ctx: CanvasRenderingContext2D, w: number, h: number, t: number, a: NonNullable<Plan["aurora"]>) {
  ctx.globalCompositeOperation = "lighter";
  const step = Math.max(3, Math.round(w / 260));
  a.colors.forEach((c, bi) => {
    const rgb = hex(c);
    for (let x = 0; x < w; x += step) {
      const u = x / w;
      const yc = h * (0.28 + bi * 0.05 + Math.sin(u * Math.PI * (1.4 + bi * 0.6) + t * 0.3 * (bi % 2 ? -1 : 1)) * 0.07);
      const hh = h * (0.28 - bi * 0.05) * (0.65 + 0.35 * Math.sin(u * 7.1 + t * 0.33 + bi));
      const k = a.strength * 0.22 * (0.6 + 0.4 * Math.sin(u * 13 + t * 0.6));
      const g = ctx.createLinearGradient(0, yc - hh, 0, yc + hh * 0.2);
      g.addColorStop(0, css(rgb, 0));
      g.addColorStop(0.75, css(rgb, k));
      g.addColorStop(1, css(rgb, 0));
      ctx.fillStyle = g;
      ctx.fillRect(x, yc - hh, step + 1, hh * 1.2);
    }
  });
  ctx.globalCompositeOperation = "source-over";
}

function smoothBody(ctx: CanvasRenderingContext2D, w: number, h: number, b: NonNullable<Plan["body"]>) {
  const x = w * b.x;
  const y = h * b.y;
  const r = Math.min(w, h) * b.size;
  if (b.glow) {
    const g = ctx.createRadialGradient(x, y, r * 0.5, x, y, r * 4);
    g.addColorStop(0, css(hex(b.glow), 0.35));
    g.addColorStop(1, css(hex(b.glow), 0));
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, w, h);
  }
  const g = ctx.createRadialGradient(x - r * 0.35, y - r * 0.4, r * 0.1, x, y, r);
  g.addColorStop(0, lighten(b.color, 0.25));
  g.addColorStop(1, b.kind === "planet" ? darken(b.color, 0.6) : b.color);
  ctx.fillStyle = g;
  ctx.beginPath();
  ctx.arc(x, y, r, 0, Math.PI * 2);
  ctx.fill();
  if (b.ring) {
    ctx.strokeStyle = css(hex(b.ring), 0.7);
    ctx.lineWidth = Math.max(1, r * 0.06);
    ctx.beginPath();
    ctx.ellipse(x, y, r * 1.8, r * 0.42, -0.25, 0, Math.PI * 2);
    ctx.stroke();
  }
}

function smoothGrid(ctx: CanvasRenderingContext2D, w: number, h: number, t: number, top: number, c: string) {
  ctx.strokeStyle = css(hex(c), 0.55);
  ctx.lineWidth = 1.2;
  const vx = w / 2;
  for (let i = -24; i <= 24; i++) {
    ctx.beginPath();
    ctx.moveTo(vx + i * 6, top);
    ctx.lineTo(vx + i * w * 0.09, h);
    ctx.stroke();
  }
  const speed = (t * 0.35) % 1;
  for (let i = 0; i < 18; i++) {
    const k = (i + speed) / 18;
    const y = top + (h - top) * k * k;
    ctx.globalAlpha = Math.min(1, k * 2.2);
    ctx.beginPath();
    ctx.moveTo(0, y);
    ctx.lineTo(w, y);
    ctx.stroke();
  }
  ctx.globalAlpha = 1;
}

// ---------------------------------------------------------------------------

export function composeScene(spec: SceneSpec | null | undefined, opts: { dark?: boolean; seed?: number } = {}): Scene {
  const p = plan(spec, opts);
  return p.style === "abstract" ? abstractScene(p) : pixelScene(p);
}
